import React, { useState } from 'react';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Wallet,
  LineChart,
  Target,
  CreditCard,
  AlertCircle,
  Calendar,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Lightbulb
} from 'lucide-react';

/**
 * AIInsights Component
 * Displays AI-generated insights and recommendations from wealth tracking data
 */
const AIInsights = ({ analysis, isDark }) => {
  const [expandedRecommendations, setExpandedRecommendations] = useState(new Set());

  if (!analysis || (!analysis.insights?.length && !analysis.recommendations?.length)) {
    return null;
  }

  const { insights, recommendations, score } = analysis;

  const toggleRecommendation = (index) => {
    const newExpanded = new Set(expandedRecommendations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRecommendations(newExpanded);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const getIconComponent = (iconName) => {
    const icons = {
      TrendingUp,
      TrendingDown,
      Wallet,
      LineChart,
      Target,
      CreditCard,
      AlertCircle,
      Calendar,
      AlertTriangle,
      Sparkles
    };
    return icons[iconName] || Sparkles;
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'positive':
        return {
          bg: isDark ? 'bg-green-900/20' : 'bg-green-50',
          border: 'border-green-500/30',
          icon: 'text-green-500',
          text: isDark ? 'text-green-300' : 'text-green-700'
        };
      case 'warning':
        return {
          bg: isDark ? 'bg-yellow-900/20' : 'bg-yellow-50',
          border: 'border-yellow-500/30',
          icon: 'text-yellow-500',
          text: isDark ? 'text-yellow-300' : 'text-yellow-700'
        };
      case 'neutral':
        return {
          bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
          border: 'border-blue-500/30',
          icon: 'text-blue-500',
          text: isDark ? 'text-blue-300' : 'text-blue-700'
        };
      default:
        return {
          bg: isDark ? 'bg-gray-800' : 'bg-gray-50',
          border: 'border-gray-500/30',
          icon: 'text-gray-500',
          text: isDark ? 'text-gray-300' : 'text-gray-700'
        };
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: {
        bg: isDark ? 'bg-red-900/30' : 'bg-red-100',
        text: isDark ? 'text-red-400' : 'text-red-700',
        label: 'High Priority'
      },
      medium: {
        bg: isDark ? 'bg-orange-900/30' : 'bg-orange-100',
        text: isDark ? 'text-orange-400' : 'text-orange-700',
        label: 'Medium Priority'
      },
      low: {
        bg: isDark ? 'bg-blue-900/30' : 'bg-blue-100',
        text: isDark ? 'text-blue-400' : 'text-blue-700',
        label: 'Low Priority'
      }
    };
    return badges[priority] || badges.medium;
  };

  return (
    <div className="space-y-4">
      {/* Header with Score */}
      <div
        className={`rounded-lg border p-4 sm:p-6 ${
          isDark ? 'bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/30' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200'
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
              <Sparkles className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                AI-Powered Wealth Insights
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Personalized analysis of your wealth tracking data
              </p>
            </div>
          </div>

          {score !== null && (
            <div className="text-right">
              <div className={`text-3xl sm:text-4xl font-bold ${getScoreColor(score)}`}>
                {score}
              </div>
              <div className={`text-xs sm:text-sm font-semibold mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {getScoreLabel(score)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Insights Grid */}
      {insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.map((insight, index) => {
            const colors = getInsightColor(insight.type);
            const IconComponent = getIconComponent(insight.icon);

            return (
              <div
                key={index}
                className={`rounded-lg border p-4 ${colors.bg} ${colors.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${colors.icon} shrink-0`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {insight.title}
                      </h4>
                      {insight.category && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-white/50'} ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {insight.category}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs sm:text-sm ${colors.text}`}>
                      {insight.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <Lightbulb className={`w-5 h-5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Personalized Recommendations
              </h4>
            </div>
          </div>

          <div className="divide-y divide-gray-700">
            {recommendations.map((rec, index) => {
              const isExpanded = expandedRecommendations.has(index);
              const badge = getPriorityBadge(rec.priority);

              return (
                <div key={index} className={`p-4 ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                  <button
                    onClick={() => toggleRecommendation(index)}
                    className="w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h5 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {rec.title}
                          </h5>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                        </div>
                        <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {rec.description}
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className={`w-5 h-5 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                      ) : (
                        <ChevronDown className={`w-5 h-5 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                      )}
                    </div>
                  </button>

                  {isExpanded && rec.actionItems && (
                    <div className={`mt-3 pl-4 border-l-2 ${isDark ? 'border-blue-500' : 'border-blue-400'}`}>
                      <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Action Steps:
                      </p>
                      <ul className="space-y-1.5">
                        {rec.actionItems.map((action, actionIndex) => (
                          <li
                            key={actionIndex}
                            className={`text-xs sm:text-sm flex items-start gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                          >
                            <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInsights;
